import { APIEntityType, type ArdoqId } from '@ardoq/api-types';
import type {
  EnhancedScopeData,
  FieldValueScopeSource,
} from '@ardoq/data-model';
import {
  legacyAffectsTypeNames,
  toNumericFieldValue,
  _ComponentDefaultFilterKeys,
  type SortedValuesOrNull,
} from '@ardoq/filter-helpers';
import {
  componentUtils,
  referenceUtils,
  enhancedScopeDataOperations,
} from '@ardoq/scope-data';

type EntityType = APIEntityType.COMPONENT | APIEntityType.REFERENCE;

/** Singleton cache for field sorted values per entity type */
export class FieldValuesCache {
  static Instance = new FieldValuesCache();

  // TODO(typeName-migration): Remove affectsTypeName param; use affectsTypeNames directly.
  static buildCacheKey(
    fieldName: string,
    entityType: EntityType,
    affectsTypeName?: string,
    affectsTypeNames?: string[]
  ): string {
    const typeKey = (
      legacyAffectsTypeNames(affectsTypeName, affectsTypeNames) ?? []
    )
      .toSorted()
      .join(',');
    return `${entityType}:${typeKey}:${fieldName}`;
  }

  private _cache = new WeakMap<
    FieldValueScopeSource,
    Map<string, SortedValuesOrNull>
  >();

  get(source: FieldValueScopeSource, cacheKey: string): SortedValuesOrNull {
    return this._cache.get(source)?.get(cacheKey) ?? null;
  }

  has(source: FieldValueScopeSource, cacheKey: string): boolean {
    return this._cache.get(source)?.has(cacheKey) ?? false;
  }

  set(
    source: FieldValueScopeSource,
    cacheKey: string,
    entry: SortedValuesOrNull
  ) {
    let scopeCache = this._cache.get(source);
    if (!scopeCache) {
      scopeCache = new Map<string, SortedValuesOrNull>();
      this._cache.set(source, scopeCache);
    }
    scopeCache.set(cacheKey, entry);
  }

  reset() {
    this._cache = new WeakMap();
  }
}

export const fieldValuesCache = FieldValuesCache.Instance;

const getComponentTypeName = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): string | null =>
  componentUtils.getType(scopeData, componentId)?.name ?? null;

const getReferenceTypeName = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId
): string | null => {
  const reference = enhancedScopeDataOperations.getReference(
    scopeData,
    referenceId
  );
  if (!reference) {
    return null;
  }
  return (
    enhancedScopeDataOperations.getReferenceType(scopeData, reference)?.name ??
    null
  );
};

type GetFieldSortedValuesArgs = {
  /**
   * The viewpoint dataset to range over; `null` outside viewpoint mode, in
   * which case there are no scoped values to collect.
   */
  scopeSource: FieldValueScopeSource | null;
  fieldName: string;
  entityType: EntityType;
  // TODO(typeName-migration): Remove affectsTypeName; use affectsTypeNames directly.
  affectsTypeName?: string;
  affectsTypeNames?: string[];
};

/**
 * Retrieves sorted numeric values for a given field and entity type from the
 * threaded viewpoint scope source. Returns the sorted array, or null when no
 * numeric values are found (including outside viewpoint mode, where there is
 * no scope source).
 */
export function getFieldSortedValues({
  scopeSource,
  fieldName,
  entityType,
  affectsTypeName,
  affectsTypeNames,
}: GetFieldSortedValuesArgs): SortedValuesOrNull {
  if (!scopeSource) {
    return null;
  }
  const { scopeData } = scopeSource;
  const isComponentType = entityType === APIEntityType.COMPONENT;
  const scopeIds = isComponentType
    ? scopeSource.scopeComponentIds
    : scopeSource.scopeReferenceIds;
  // TODO(typeName-migration): Remove affectsTypeName param; use affectsTypeNames directly.
  const typeFilter = legacyAffectsTypeNames(affectsTypeName, affectsTypeNames);

  const values: number[] = [];

  for (const currentId of scopeIds) {
    if (typeFilter) {
      const entityTypeName = isComponentType
        ? getComponentTypeName(scopeData, currentId)
        : getReferenceTypeName(scopeData, currentId);

      if (!entityTypeName || !typeFilter.includes(entityTypeName)) {
        continue;
      }
    }

    const rawValue =
      fieldName === _ComponentDefaultFilterKeys.INCOMING_REF_COUNT
        ? componentUtils.getSources(scopeData, currentId).length
        : fieldName === _ComponentDefaultFilterKeys.OUTGOING_REF_COUNT
          ? componentUtils.getTargets(scopeData, currentId).length
          : isComponentType
            ? (componentUtils.getAttribute(scopeData, currentId, fieldName) ??
              null)
            : (referenceUtils.getAttribute(scopeData, currentId, fieldName) ??
              null);

    const num = toNumericFieldValue(rawValue);
    if (num !== undefined) {
      values.push(num);
    }
  }

  if (!values.length) {
    return null;
  }

  return values.sort((a, b) => a - b);
}
